import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Upload, Lock, Users, Download } from 'lucide-react';

// ─────────────────────────────────────────────
// STEP DATA
// ─────────────────────────────────────────────
const STEPS = [
  {
    number: '01',
    icon: Upload,
    title: 'Upload & Draft',
    description:
      'Bring in your existing approved policies, or draft new ones in Policy Builder grounded in your regulatory docs.',
    detail: 'Already have finished policies? Import them as approved — no re-approval needed.',
  },
  {
    number: '02',
    icon: Lock,
    title: 'Approve & Lock',
    description:
      'Your review team approves. Policy becomes an immutable, timestamped record.',
    detail: 'Once locked, nothing changes retroactively. Every version is preserved forever.',
  },
  {
    number: '03',
    icon: Users,
    title: 'Staff Acknowledge',
    description:
      'Staff get notified automatically. One-click acknowledgment. Tracked with timestamps.',
    detail: 'Every staff member. Every policy. Every date. Proof ready for any auditor.',
  },
  {
    number: '04',
    icon: Download,
    title: 'Export for Audit',
    description:
      'Click one button. Complete package in 30 seconds. Hand it to the auditor. Done.',
    detail: 'PDF + CSV. Every policy, every version, every acknowledgment. No scrambling.',
  },
];

// ─────────────────────────────────────────────
// WHISPER TEAL MESH BACKGROUND
// Much lower opacity/saturation than the solution
// card version — atmosphere, not color. Gives the
// section warmth without competing with the hero.
// ─────────────────────────────────────────────
const WhisperMesh = () => (
  <>
    <style>{`
      @keyframes whisperMesh {
        0%   { background-position: 0% 0%; }
        25%  { background-position: 100% 30%; }
        50%  { background-position: 70% 100%; }
        75%  { background-position: 20% 60%; }
        100% { background-position: 0% 0%; }
      }
      @keyframes whisperOrb1 {
        0%,100% { transform: translate(0,0) scale(1); }
        50%     { transform: translate(60px,-40px) scale(1.2); }
      }
      @keyframes whisperOrb2 {
        0%,100% { transform: translate(0,0) scale(1); }
        50%     { transform: translate(-50px,60px) scale(1.15); }
      }
      @keyframes whisperOrb3 {
        0%,100% { transform: translate(0,0) scale(1); }
        33%     { transform: translate(40px,50px) scale(1.1); }
        66%     { transform: translate(-30px,-20px) scale(0.95); }
      }
    `}</style>

    <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 0 }}>
      {/* Base — barely-there teal tint, shifts slowly */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(135deg, #f8fffe 0%, #ffffff 35%, #f0fdfa 65%, #f8fffe 100%)',
        backgroundSize: '300% 300%',
        animation: 'whisperMesh 20s ease-in-out infinite',
      }} />

      {/* Orb 1 — soft teal top-right, very low opacity */}
      <div style={{
        position: 'absolute', top: '-5%', right: '5%',
        width: '45%', height: '50%', borderRadius: '50%',
        filter: 'blur(80px)',
        animation: 'whisperOrb1 14s ease-in-out infinite',
        background: 'radial-gradient(circle, rgba(51,153,153,0.09) 0%, transparent 70%)',
      }} />

      {/* Orb 2 — whisper teal bottom-left */}
      <div style={{
        position: 'absolute', bottom: '0%', left: '0%',
        width: '40%', height: '45%', borderRadius: '50%',
        filter: 'blur(90px)',
        animation: 'whisperOrb2 17s ease-in-out infinite',
        background: 'radial-gradient(circle, rgba(13,148,136,0.07) 0%, transparent 70%)',
      }} />

      {/* Orb 3 — white shimmer center, adds depth without color */}
      <div style={{
        position: 'absolute', top: '30%', left: '25%',
        width: '50%', height: '40%', borderRadius: '50%',
        filter: 'blur(100px)',
        animation: 'whisperOrb3 19s ease-in-out infinite',
        background: 'radial-gradient(circle, rgba(255,255,255,0.6) 0%, transparent 70%)',
      }} />
    </div>
  </>
);

// ─────────────────────────────────────────────
// ANIMATED TIMELINE LINE
// scaleY driven by scroll — grows downward as
// user scrolls through the section.
// ─────────────────────────────────────────────
const TimelineLine = ({ containerRef }) => {
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start 0.75', 'end 0.6'],
  });

  const scaleY = useTransform(scrollYProgress, [0, 1], [0, 1]);
  const opacity = useTransform(scrollYProgress, [0, 0.04], [0, 1]);

  return (
    <div
      className="absolute top-0 bottom-0 hidden sm:block pointer-events-none"
      style={{ left: '50%', transform: 'translateX(-50%)', width: '2px' }}
    >
      {/* Static grey track */}
      <div className="absolute inset-0" style={{
        background: 'linear-gradient(to bottom, transparent 0%, #cbd5e1 6%, #cbd5e1 94%, transparent 100%)',
      }} />

      {/* Animated teal fill */}
      <motion.div
        className="absolute top-0 left-0 right-0"
        style={{
          height: '100%',
          background: 'linear-gradient(to bottom, #339999, #06b6d4)',
          scaleY,
          opacity,
          transformOrigin: 'top center',
        }}
      />
    </div>
  );
};

// ─────────────────────────────────────────────
// STEP NODE
// Oversized step number — landmark not label.
// White icon against dark navy circle.
// ─────────────────────────────────────────────
const StepNode = ({ number, icon: Icon, index }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.4 }}
    whileInView={{ opacity: 1, scale: 1 }}
    viewport={{ once: true, margin: '-60px' }}
    transition={{ duration: 0.45, delay: 0.05, ease: 'backOut' }}
    className="relative z-10 flex flex-col items-center flex-shrink-0"
    style={{ gap: '6px' }}
  >
    {/* Step number — oversized, Inter Black */}
    {/* Large enough to feel like a landmark, not a footnote */}
    <div style={{
      fontFamily: 'Inter, sans-serif',
      fontWeight: 900,
      fontSize: '28px',
      letterSpacing: '-0.03em',
      color: '#339999',
      lineHeight: 1,
      // Subtle ghost behind the number for depth
      textShadow: '0 0 20px rgba(51,153,153,0.2)',
    }}>
      {number}
    </div>

    {/* Circle node */}
    <div
      className="flex items-center justify-center"
      style={{
        width: '60px',
        height: '60px',
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #00022e 0%, #02075d 100%)',
        border: '2px solid #339999',
        boxShadow: '0 0 0 5px rgba(51,153,153,0.1), 0 4px 20px rgba(2,7,93,0.3)',
      }}
    >
      {/* White icon — maximum contrast on dark navy */}
      <Icon className="w-5 h-5" style={{ color: '#ffffff' }} />
    </div>
  </motion.div>
);

// ─────────────────────────────────────────────
// STEP CARD
// Inter Black headers, consistent with Section 2
// ─────────────────────────────────────────────
const StepCard = ({ step, index }) => {
  const isEven = index % 2 === 0;

  return (
    <motion.div
      initial={{ opacity: 0, x: isEven ? -28 : 28 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.6, delay: 0.15, ease: [0.23, 1, 0.32, 1] }}
      whileHover={{
        y: -4,
        boxShadow: '0 8px 32px rgba(51,153,153,0.12), 0 2px 8px rgba(0,0,0,0.06)',
        borderColor: '#99f6e4',
        transition: { duration: 0.25 },
      }}
      style={{
        background: 'rgba(255,255,255,0.85)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        border: '1.5px solid rgba(226,232,240,0.8)',
        boxShadow: '0 2px 16px rgba(0,0,0,0.05)',
        borderRadius: '16px',
        padding: '20px 24px',
        maxWidth: '400px',
        cursor: 'default',
        flex: 1,
      }}
    >
      {/* Title — Inter Black */}
      <h3 style={{
        fontFamily: 'Inter, sans-serif',
        fontWeight: 900,
        fontSize: '20px',
        color: '#0f172a',
        letterSpacing: '-0.02em',
        marginBottom: '10px',
        lineHeight: 1.2,
      }}>
        {step.title}
      </h3>

      {/* Description */}
      <p style={{
        fontFamily: 'Inter, sans-serif',
        fontWeight: 400,
        fontSize: '15px',
        color: '#475569',
        lineHeight: 1.65,
        marginBottom: '14px',
      }}>
        {step.description}
      </p>

      {/* Detail — teal accent */}
      <p style={{
        fontFamily: 'Inter, sans-serif',
        fontWeight: 500,
        fontSize: '13px',
        color: '#339999',
        lineHeight: 1.6,
        paddingTop: '14px',
        borderTop: '1px solid rgba(241,245,249,0.9)',
      }}>
        {step.detail}
      </p>
    </motion.div>
  );
};

// ─────────────────────────────────────────────
// STEP ROW
// Desktop: alternating left/right
// Mobile: node left, card right
// ─────────────────────────────────────────────
const StepRow = ({ step, index }) => {
  const isEven = index % 2 === 0;

  return (
    <div className="relative w-full">

      {/* ── Mobile ── */}
      <div className="flex sm:hidden items-start gap-5 w-full">
        <div className="flex flex-col items-center flex-shrink-0 mt-1">
          <StepNode number={step.number} icon={step.icon} index={index} />
        </div>
        <div className="flex-1">
          <StepCard step={step} index={index} />
        </div>
      </div>

      {/* ── Desktop — alternating ── */}
      <div className="hidden sm:flex items-center w-full">

        {/* Left slot */}
        <div className="flex-1 flex justify-end pr-10">
          {isEven
            ? <StepCard step={step} index={index} />
            : <div style={{ maxWidth: '400px', width: '100%' }} />
          }
        </div>

        {/* Center node */}
        <div className="flex items-center justify-center flex-shrink-0 relative z-10"
          style={{ width: '80px' }}>
          <StepNode number={step.number} icon={step.icon} index={index} />
        </div>

        {/* Right slot */}
        <div className="flex-1 flex justify-start pl-10">
          {!isEven
            ? <StepCard step={step} index={index} />
            : <div style={{ maxWidth: '400px', width: '100%' }} />
          }
        </div>

      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// HOW IT WORKS SECTION
// ─────────────────────────────────────────────
export const HowItWorksSection = () => {
  const containerRef = useRef(null);

  return (
    <section
      id="how-it-works"
      className="w-full py-24 px-6 md:px-12 overflow-hidden relative"
      style={{ background: '#f8fffe' }}
    >
      {/* Whisper teal mesh — atmosphere only */}
      <WhisperMesh />

      <div className="max-w-5xl mx-auto relative" style={{ zIndex: 1 }}>

        {/* ── Section Header ── */}
        <div className="text-center mb-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.23, 1, 0.32, 1] }}
          >
            <h2 style={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 900,
              fontSize: 'clamp(2rem, 5vw, 3.5rem)',
              color: '#0f172a',
              lineHeight: 1.1,
              letterSpacing: '-0.02em',
              marginBottom: '0',
            }}>
              Four Steps.
              <br />
              <span style={{ color: '#339999' }}>
                Then You're Audit-Ready Forever.
              </span>
            </h2>

            <motion.div
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.35, ease: 'easeOut' }}
              style={{
                height: '3px',
                width: '48px',
                borderRadius: '99px',
                background: 'linear-gradient(90deg, #339999, #06b6d4)',
                margin: '20px auto 0',
                transformOrigin: 'left center',
              }}
            />
          </motion.div>
        </div>

        {/* ── Timeline ── */}
        <div
          ref={containerRef}
          className="relative flex flex-col"
          style={{ gap: '48px' }}
        >
          <TimelineLine containerRef={containerRef} />

          {STEPS.map((step, index) => (
            <StepRow key={step.number} step={step} index={index} />
          ))}
        </div>

        {/* ── Bottom close ── */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="text-center mt-20"
        >
          <p style={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 400,
            fontSize: '17px',
            color: '#64748b',
          }}>
            Setup takes minutes.{' '}
            <span style={{ color: '#339999', fontWeight: 700 }}>
              Audit confidence lasts forever.
            </span>
          </p>
        </motion.div>

      </div>
    </section>
  );
};

export default HowItWorksSection;
